import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { MdAutoAwesome, MdDownload, MdPlayArrow, MdCheck, MdLock } from 'react-icons/md';
import { FaYoutube, FaFacebook, FaTiktok, FaInstagram } from 'react-icons/fa';

const platforms = [
  { id: 'youtube', label: 'YouTube Long', icon: <FaYoutube style={{ color: '#ff0000' }} />, color: '#ff0000', bg: 'rgba(255,0,0,0.08)', border: 'rgba(255,0,0,0.2)' },
  { id: 'youtube_shorts', label: 'YouTube Shorts', icon: <FaYoutube style={{ color: '#ff4444' }} />, color: '#ff4444', bg: 'rgba(255,68,68,0.08)', border: 'rgba(255,68,68,0.2)' },
  { id: 'tiktok', label: 'TikTok', icon: <FaTiktok style={{ color: '#e0e0e0' }} />, color: '#e0e0e0', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.1)' },
  { id: 'facebook', label: 'Facebook', icon: <FaFacebook style={{ color: '#1877f2' }} />, color: '#1877f2', bg: 'rgba(24,119,242,0.08)', border: 'rgba(24,119,242,0.2)' },
  { id: 'instagram', label: 'Instagram', icon: <FaInstagram style={{ color: '#e1306c' }} />, color: '#e1306c', bg: 'rgba(225,48,108,0.08)', border: 'rgba(225,48,108,0.2)' }
];

const thumbnailStyles = [
  { id: 'dramatic', label: 'Dramatic' },
  { id: 'minimalist', label: 'Minimalist' },
  { id: 'colorful', label: 'Colorful' },
  { id: 'dark', label: 'Dark Cinematic' },
  { id: 'bright', label: 'Bright Bold' }
];

const stages = [
  { id: 'script', number: 1, label: 'Script Architect', icon: '✍️', description: 'Full retention optimised script with PAS framework' },
  { id: 'production', number: 2, label: 'Production Blueprint', icon: '🎬', description: 'Voice settings, B-roll terms, color grades' },
  { id: 'thumbnail', number: 3, label: 'Visual Studio', icon: '🖼️', description: 'AI generated thumbnail concepts and image' },
  { id: 'ctr', number: 4, label: 'CTR Engineer', icon: '📈', description: 'Titles, descriptions, tags optimised for clicks' },
  { id: 'brief', number: 5, label: 'Production Brief', icon: '📋', description: 'Everything compiled into downloadable brief' }
];

const ContentForge = () => {
  const [topic, setTopic] = useState('');
  const [niche, setNiche] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('youtube');
  const [selectedStyle, setSelectedStyle] = useState('dramatic');
  const [activeStage, setActiveStage] = useState(null);
  const [completedStages, setCompletedStages] = useState({});
  const [loadingStage, setLoadingStage] = useState(null);
  const [results, setResults] = useState({});
  const [generatedImage, setGeneratedImage] = useState(null);

  const platform = platforms.find(p => p.id === selectedPlatform);

  const runStage = async (stageId) => {
    if (!topic.trim()) {
      toast.error('Please enter a topic first');
      return;
    }

    if (stageId === 'production' && !completedStages.script) {
      toast.error('Please generate the script first');
      return;
    }

    if (stageId === 'thumbnail' && !completedStages.script) {
      toast.error('Please generate the script first');
      return;
    }

    if (stageId === 'ctr' && !completedStages.script) {
      toast.error('Please generate the script first');
      return;
    }

    if (stageId === 'brief' && !completedStages.ctr) {
      toast.error('Please complete CTR Engineer first');
      return;
    }

    setLoadingStage(stageId);
    setActiveStage(stageId);

    try {
      const endpoints = {
        script: '/api/contentforge/script',
        production: '/api/contentforge/production',
        thumbnail: '/api/contentforge/thumbnail',
        ctr: '/api/contentforge/ctr',
        brief: '/api/contentforge/brief'
      };

      const payloads = {
        script: { topic, platform: selectedPlatform, niche, target_audience: targetAudience },
        production: { topic, platform: selectedPlatform, niche, script: results.script || '' },
        thumbnail: { topic, platform: selectedPlatform, niche, style: selectedStyle },
        ctr: { topic, platform: selectedPlatform, niche, script_excerpt: results.script || '' },
        brief: {
          topic, platform: selectedPlatform, niche,
          script: results.script || '',
          production: results.production || '',
          ctr_data: results.ctr_data || '',
          thumbnail_concepts: results.thumbnail_concepts || ''
        }
      };

      const response = await fetch(`https://vantage-production-4475.up.railway.app${endpoints[stageId]}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payloads[stageId])
      });

      const data = await response.json();

      if (data.success) {
        const newResults = { ...results };

        if (stageId === 'script') newResults.script = data.data.script;
        if (stageId === 'production') newResults.production = data.data.production;
        if (stageId === 'thumbnail') {
          newResults.thumbnail_concepts = data.data.thumbnail_concepts;
          if (data.data.generated_image_url) setGeneratedImage(data.data.generated_image_url);
        }
        if (stageId === 'ctr') newResults.ctr_data = data.data.ctr_data;
        if (stageId === 'brief') newResults.brief = data.data.brief;

        setResults(newResults);
        setCompletedStages(prev => ({ ...prev, [stageId]: true }));
        toast.success(stages.find(s => s.id === stageId)?.label + ' complete!');
      } else {
        toast.error(data.message || 'Stage failed');
      }
    } catch (error) {
      toast.error('Failed: ' + error.message);
    } finally {
      setLoadingStage(null);
    }
  };

  const downloadBrief = () => {
    if (!results.brief) {
      toast.error('Generate the production brief first');
      return;
    }

    const content = `VANTAGE CONTENT FORGE — PRODUCTION BRIEF
Generated: ${new Date().toLocaleDateString()}
Topic: ${topic}
Platform: ${selectedPlatform.toUpperCase()}
Niche: ${niche}

${'='.repeat(60)}
STAGE 1 — SCRIPT
${'='.repeat(60)}
${results.script || 'Not generated'}

${'='.repeat(60)}
STAGE 2 — PRODUCTION BLUEPRINT
${'='.repeat(60)}
${results.production || 'Not generated'}

${'='.repeat(60)}
STAGE 3 — THUMBNAIL CONCEPTS
${'='.repeat(60)}
${results.thumbnail_concepts || 'Not generated'}

${'='.repeat(60)}
STAGE 4 — CTR ENGINEER
${'='.repeat(60)}
${results.ctr_data || 'Not generated'}

${'='.repeat(60)}
STAGE 5 — PRODUCTION BRIEF
${'='.repeat(60)}
${results.brief || 'Not generated'}`;

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ContentForge_${topic.replace(/\s+/g, '_')}_${selectedPlatform}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Production brief downloaded!');
  };

  const getStageResult = (stageId) => {
    if (stageId === 'script') return results.script;
    if (stageId === 'production') return results.production;
    if (stageId === 'thumbnail') return results.thumbnail_concepts;
    if (stageId === 'ctr') return results.ctr_data;
    if (stageId === 'brief') return results.brief;
    return null;
  };

  const completedCount = Object.keys(completedStages).length;

  return (
    <div className="page-container">

      <div className="page-header">
        <h1><span className="gradient-text">ContentForge</span></h1>
        <p>AI powered content production pipeline from idea to ready to upload</p>
      </div>

      <div style={{
        background: 'rgba(0,200,83,0.08)', border: '1px solid rgba(0,200,83,0.2)',
        borderRadius: '14px', padding: '20px 24px', marginBottom: '24px'
      }}>
        <div style={{ fontWeight: '700', fontSize: '15px', color: 'var(--primary)', marginBottom: '8px' }}>
          What is ContentForge?
        </div>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.8', margin: 0 }}>
          ContentForge is your complete AI production pipeline. Enter a topic and run each stage to get a
          <strong style={{ color: 'var(--text-primary)' }}> full script, production blueprint, AI generated thumbnail, CTR optimised titles </strong>
          and a downloadable production brief — all powered by Obsidian AI and Muapi. Go from idea to ready to record in minutes.
        </p>
      </div>

      <div className="grid-2" style={{ gap: '24px' }}>

        {/* Left — Setup */}
        <div>
          <div className="card" style={{ marginBottom: '20px' }}>
            <div className="card-header">
              <span className="card-title">Video Setup</span>
              {completedCount > 0 && (
                <span className="badge badge-success">{completedCount}/5 stages done</span>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Video Topic *</label>
              <input className="form-control"
                placeholder="e.g. 10 Habits That Changed My Life"
                value={topic}
                onChange={(e) => setTopic(e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Niche</label>
              <input className="form-control"
                placeholder="e.g. Self improvement, Finance, Tech, Gaming"
                value={niche}
                onChange={(e) => setNiche(e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Target Audience</label>
              <input className="form-control"
                placeholder="e.g. 18-35 year old males interested in productivity"
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)} />
            </div>

            <div className="form-group">
              <label className="form-label">Platform</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {platforms.map(p => (
                  <div key={p.id}
                    onClick={() => setSelectedPlatform(p.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 14px', borderRadius: '10px', cursor: 'pointer',
                      background: selectedPlatform === p.id ? p.bg : 'var(--bg-dark)',
                      border: '1px solid ' + (selectedPlatform === p.id ? p.border : 'var(--border)'),
                      transition: 'all 0.2s ease'
                    }}>
                    {p.icon}
                    <span style={{ fontSize: '13px', fontWeight: '600', color: selectedPlatform === p.id ? p.color : 'var(--text-primary)' }}>
                      {p.label}
                    </span>
                    {selectedPlatform === p.id && (
                      <MdCheck style={{ marginLeft: 'auto', color: p.color }} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Thumbnail Style</label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {thumbnailStyles.map(s => (
                  <button key={s.id}
                    onClick={() => setSelectedStyle(s.id)}
                    className={selectedStyle === s.id ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Pipeline Progress</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {stages.map((stage, index) => {
                const isCompleted = completedStages[stage.id];
                const isLoading = loadingStage === stage.id;
                const isLocked = stage.id !== 'script' && !completedStages.script && stage.id !== 'thumbnail';

                return (
                  <div key={stage.id} style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 14px', borderRadius: '10px',
                    background: isCompleted ? 'rgba(0,200,83,0.08)' : 'var(--bg-dark)',
                    border: '1px solid ' + (isCompleted ? 'rgba(0,200,83,0.3)' : 'var(--border)'),
                    transition: 'all 0.2s ease'
                  }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: isCompleted ? '#00c853' : 'var(--bg-card)',
                      border: '2px solid ' + (isCompleted ? '#00c853' : 'var(--border)'),
                      fontSize: '14px', fontWeight: '800', color: isCompleted ? 'white' : 'var(--text-secondary)',
                      flexShrink: 0
                    }}>
                      {isCompleted ? <MdCheck /> : stage.number}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {stage.icon} {stage.label}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                        {stage.description}
                      </div>
                    </div>
                    {isLoading ? (
                      <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
                    ) : isCompleted ? (
                      <span style={{ fontSize: '11px', color: '#00c853', fontWeight: '700' }}>Done</span>
                    ) : (
                      <span style={{ fontSize: '11px', color: 'var(--text-light)' }}>Pending</span>
                    )}
                  </div>
                );
              })}
            </div>

            {completedCount === 5 && (
              <button className="btn btn-primary" onClick={downloadBrief}
                style={{ marginTop: '16px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <MdDownload /> Download Production Brief
              </button>
            )}
          </div>
        </div>

        {/* Right — Stages */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {stages.map((stage) => {
            const isCompleted = completedStages[stage.id];
            const isLoading = loadingStage === stage.id;
            const result = getStageResult(stage.id);

            return (
              <div key={stage.id} className="card" style={{
                border: '1px solid ' + (isCompleted ? 'rgba(0,200,83,0.3)' : activeStage === stage.id ? 'rgba(0,200,83,0.2)' : 'var(--border)')
              }}>
                <div className="card-header">
                  <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px' }}>{stage.icon}</span>
                    Stage {stage.number} — {stage.label}
                    {isCompleted && <span className="badge badge-success" style={{ fontSize: '10px' }}>Complete</span>}
                  </span>
                  <button
                    className={isCompleted ? 'btn btn-secondary btn-sm' : 'btn btn-primary btn-sm'}
                    onClick={() => runStage(stage.id)}
                    disabled={isLoading || loadingStage !== null}
                    style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    {isLoading ? (
                      <><div className="spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} /> Running...</>
                    ) : isCompleted ? (
                      <><MdPlayArrow /> Regenerate</>
                    ) : (
                      <><MdPlayArrow /> Run Stage</>
                    )}
                  </button>
                </div>

                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                  {stage.description}
                </p>

                {isLoading && (
                  <div style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', padding: '40px', gap: '12px'
                  }}>
                    <div className="spinner" style={{ width: '28px', height: '28px', borderWidth: '3px' }} />
                    <p style={{ color: 'var(--primary)', fontSize: '13px' }}>
                      Obsidian AI is working on {stage.label}...
                    </p>
                  </div>
                )}

                {result && !isLoading && (
                  <div style={{
                    background: 'var(--bg-dark)', borderRadius: '10px',
                    padding: '16px', border: '1px solid var(--border)',
                    maxHeight: '300px', overflowY: 'auto',
                    fontSize: '13px', lineHeight: '1.8',
                    color: 'var(--text-primary)', whiteSpace: 'pre-wrap'
                  }}>
                    {result}
                  </div>
                )}

                {stage.id === 'thumbnail' && generatedImage && !isLoading && (
                  <div style={{ marginTop: '12px' }}>
                    <p style={{ fontSize: '12px', color: 'var(--primary)', marginBottom: '8px', fontWeight: '600' }}>
                      🖼️ AI Generated Thumbnail:
                    </p>
                    <img src={generatedImage} alt="Generated thumbnail"
                      style={{ width: '100%', borderRadius: '10px', border: '1px solid var(--border)' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                    <a href={generatedImage} download="thumbnail.jpg" target="_blank" rel="noopener noreferrer">
                      <button className="btn btn-secondary btn-sm" style={{ marginTop: '8px', width: '100%' }}>
                        <MdDownload /> Download Thumbnail
                      </button>
                    </a>
                  </div>
                )}

                {!result && !isLoading && (
                  <div style={{
                    textAlign: 'center', padding: '30px',
                    color: 'var(--text-light)', fontSize: '13px'
                  }}>
                    Click Run Stage to generate {stage.label}
                  </div>
                )}
              </div>
            );
          })}

          {completedCount === 5 && (
            <button className="btn btn-primary" onClick={downloadBrief}
              style={{ width: '100%', padding: '16px', fontSize: '16px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
              <MdDownload /> Download Complete Production Brief
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentForge;